import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Archive, ChevronRight, Network, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { clsx } from 'clsx';
import Collection from '@model-core/core/Collection';
import { CardGrid } from '@/components/list/CardGrid';
import { Panel } from '@/components/Panel';
import { ListStatus } from '@/components/list/ListStatus';
import { NoCompanySelected } from '@/components/list/NoCompanySelected';
import { SearchInput } from '@/components/list/SearchInput';
import { Pagination } from '@/components/table/Pagination';
import { TeamFormDialog } from '@/components/teams/TeamDialogs';
import { teamTagStyle } from '@/components/teams/teamColors';
import { ORG_GROUP_ICONS } from '@/components/roles/roleIcons';
import { useListControls } from '@/hooks/useListControls';
import { usePagedList } from '@/hooks/usePagedList';
import { useRelatedByIds } from '@/hooks/useRelatedByIds';
import TeamMemberModel from '@/models/entities/TeamMemberModel';
import TeamModel from '@/models/entities/TeamModel';
import type { PageRequest } from '@/models/listPage';
import { TEAM_MEMBER_STATUS, TEAM_ROLE, TEAM_STATUS } from '@/models/references/references';
import { useCompanyStore, useCurrentCompany } from '@/stores/useCompanyStore';
import { findReferenceId, useReferenceOptions, useReferences } from '@/stores/useReferenceOptions';
import { memberCountLabel } from './teamOrgChart';
import { TEAM_SUMMARIES } from '@/models/entities/TeamSummaryModel';

const ManagerIcon = ORG_GROUP_ICONS.manager;

/** Tailles de page : multiples de 3, la grille des cartes ayant 3 colonnes */
const TEAM_PAGE_SIZES = [12, 24, 48] as const;

/**
 * Managers actuels des équipes affichées (une requête pour la page) : [teamId => manager], null pendant le chargement.
 * Sans managers (erreur) : les cartes restent affichées, sans la ligne du manager.
 */
function useManagersOf(teamIds: string[]): Map<number, TeamMemberModel> | null {
  const managerRoleId = findReferenceId(useReferenceOptions('teamRoles'), TEAM_ROLE.MANAGER);
  const activeId = findReferenceId(useReferenceOptions('teamMemberStatuses'), TEAM_MEMBER_STATUS.ACTIVE);
  const load = useCallback(
    async (ids: string[]) => {
      const { collection } = await new Collection(TeamMemberModel)
        .setFilter('team', ids as never)
        .setFilter('teamRole', String(managerRoleId))
        .setFilter('teamMemberStatus', String(activeId))
        .list();
      // Nom du manager
      await collection.fetchResolvable('member', 'user');
      return new Map(collection.models.map((manager) => [manager.teamId, manager]));
    },
    [managerRoleId, activeId],
  );

  return useRelatedByIds(teamIds, managerRoleId && activeId ? load : null);
}

/**
 * Équipes de la société sélectionnée (Paramétrage Dirigeant), en cartes comme le prototype : nom, description, nombre de
 * membres (activeMemberCount, tenu par l'API dans TeamSummary), manager (ou « Pas de manager assigné ») ; une carte pour
 * en créer une. Paginé côté API (tri par nom) ; recherche dans le nom et la description et filtre des archivées
 * (masquées par défaut) envoyés à l'API, retour en page 1 à chaque changement.
 */
export function TeamsPage() {
  const company = useCurrentCompany();
  const companiesLoaded = useCompanyStore((state) => state.loaded);
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const companyId = company?.id;
  // Autre société : on repart de la page 1
  const list = useListControls({
    filters: { search: '', showArchived: false },
    pageSizes: TEAM_PAGE_SIZES,
    resetKey: companyId,
  });
  const { filters, search, page, itemsPerPage, changeFilters } = list;
  const activeStatusId = findReferenceId(useReferenceOptions('teamStatuses'), TEAM_STATUS.ACTIVE);

  const request = useMemo<PageRequest | null>(
    () =>
      companyId && (filters.showArchived || activeStatusId)
        ? {
            filters: {
              company: companyId,
              search,
              teamStatus: filters.showArchived ? null : activeStatusId,
            },
            order: { name: 'asc' },
            page,
            itemsPerPage,
          }
        : null,
    [companyId, search, filters.showArchived, activeStatusId, page, itemsPerPage],
  );
  // Statut des équipes (archivées) : lus par leur id dans les listes de référence (chargées une fois par session)
  useReferences('teamStatuses');
  const {
    models: teams,
    totalItems,
    loading,
    error,
  } = usePagedList(TeamModel, request, 0, { summary: TEAM_SUMMARIES });
  const managers = useManagersOf(useMemo(() => teams.map((team) => String(team.id)), [teams]));

  if (companiesLoaded && !company) {
    return <NoCompanySelected />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
        <SearchInput
          label="Rechercher une équipe"
          value={filters.search}
          onChange={(value) => changeFilters({ search: value })}
        />
        <label className="inline-flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={filters.showArchived}
            onChange={(e) => changeFilters({ showArchived: e.target.checked })}
          />
          Afficher les équipes archivées
        </label>
      </div>

      <ListStatus loading={!request || (loading && !teams.length && !totalItems)} error={error}>
        {/* Sans résultat, la carte de création reste affichée sous le message */}
        {search && !loading && totalItems === 0 && (
          <p className="text-sm text-slate-500" role="status">
            Aucune équipe ne correspond à « {search} ».
          </p>
        )}
        <CardGrid loading={loading} data-testid="team-cards">
          <button
            onClick={() => setCreating(true)}
            className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-6 flex flex-col items-center justify-center text-slate-400 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all min-h-[220px] group"
          >
            <span className="w-12 h-12 rounded-full bg-card border border-slate-200 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-sm">
              <Plus className="w-6 h-6" />
            </span>
            <span className="font-medium">Créer une nouvelle équipe</span>
          </button>

          {teams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              manager={managers?.get(Number(team.id))}
              managerLoaded={managers !== null}
            />
          ))}
        </CardGrid>
        <Panel className="overflow-hidden">
          <Pagination {...list.pagination} totalItems={totalItems} label={['équipe', 'équipes']} feminine />
        </Panel>
      </ListStatus>

      {creating && company && (
        <TeamFormDialog
          team={null}
          companyId={company.id}
          onClose={() => setCreating(false)}
          onSaved={(team) => {
            toast.success(`L'équipe ${team.name} a été créée.`);
            // Suite logique : lui donner un manager et des équipiers
            navigate(`/teams/${team.id}`);
          }}
        />
      )}
    </div>
  );
}

function TeamCard({
  team,
  manager,
  managerLoaded,
}: {
  team: TeamModel;
  manager: TeamMemberModel | undefined;
  /** Managers de la page chargés : sinon on n'affiche pas encore « Pas de manager assigné » */
  managerLoaded: boolean;
}) {
  return (
    <Link
      to={`/teams/${team.id}`}
      aria-label={`Équipe ${team.name}`}
      className={clsx(
        'group border rounded-xl p-6 shadow-sm hover:shadow-md transition-all relative flex flex-col min-h-[220px]',
        team.isActive ? 'bg-card border-slate-200 hover:border-blue-400' : 'bg-slate-50 border-slate-200 opacity-80',
      )}
    >
      <div className="flex items-start justify-between mb-4">
        {/* Aux couleurs du tag de l'équipe */}
        <div
          className="p-3 rounded-lg border"
          style={teamTagStyle(team.color)}
          data-testid="team-color"
          data-color={team.color || undefined}
        >
          {team.isActive ? <Network className="w-6 h-6" /> : <Archive className="w-6 h-6" />}
        </div>
        <span className="flex items-center gap-1 text-slate-400 group-hover:text-blue-500 transition-colors text-xs font-medium">
          Configurer
          <ChevronRight className="w-4 h-4" />
        </span>
      </div>

      <h3 className="text-lg font-bold text-slate-900 mb-1">{team.name}</h3>
      {!team.isActive && <span className="text-xs text-slate-500">{team.teamStatusLabel}</span>}
      {team.description && <p className="text-sm text-slate-500 line-clamp-2">{team.description}</p>}

      {team.isActive && (
        <div className="mt-auto pt-4 space-y-3 text-sm text-slate-600">
          <p className="flex items-center gap-3">
            <Users className="w-4 h-4 text-slate-400" />
            {memberCountLabel(team.activeMemberCount)}
          </p>
          {manager ? (
            <p className="flex items-center gap-3 text-slate-500">
              <ManagerIcon className="w-4 h-4 text-slate-400" />
              <span>
                Manager : <span className="font-medium text-slate-900">{manager.fullName}</span>
              </span>
            </p>
          ) : (
            managerLoaded && (
              <span className="inline-block text-amber-600 italic text-xs bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                Pas de manager assigné
              </span>
            )
          )}
        </div>
      )}
    </Link>
  );
}
